import { events } from "@constants/tracking";
import TTSContainer from "@memorang/features/src/TTS/TTSContainer";
import type { BaseItemSchema } from "@memorang/types";
import ToeicItemRenderer from "@memorang/ui/components/StudySession/SessionContent/item-types/ToeicItemRenderer";
import { useDevice } from "@memorang/ui/useDevice";
import { trackCustomEvent } from "analytics";
import { useLocalSearchParams } from "expo-router";
import HintContainer from "../../containers/HintContainer";
import StudySessionFeedbackContainer from "../../containers/StudySessionFeedbackContainer";
import { resolveLayoutConfig } from "../../helpers/layout-config";
import {
	hasMetadataSplitLayout,
	isListeningScenario,
} from "../../helpers/split-layout";
import { getToeicRenderingLayout } from "../../helpers/toeic-layout";
import useCurrentSession from "../../hooks/useCurrentSession";
import useItemAudioGate from "../../hooks/useItemAudioGate";
import useSessionStore from "../../hooks/useSessionStore";
import useToeicItemData from "../../hooks/useToeicItemData";

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
	isSummativeTest?: boolean;
};

const ToeicItemContainer = ({
	item,
	isSplitLayout,
	isSummativeTest,
}: Props) => {
	const { isDesktopWeb, isLargeWindowSize } = useDevice();
	const { prev } = useLocalSearchParams<{ prev?: string }>();
	const forceStackedLayout = isListeningScenario(item) && !isLargeWindowSize;
	const shouldRenderItemMediaInLeftPanel = hasMetadataSplitLayout(item);
	const layoutConfig = resolveLayoutConfig(item);
	const renderingLayout = layoutConfig
		? getToeicRenderingLayout({
				item,
				isSplitLayout,
				layoutConfig,
				forceStacked: forceStackedLayout,
			})
		: undefined;
	const hasStimulusPanel = Boolean(
		(renderingLayout?.effectiveSplitLayout || forceStackedLayout) &&
			renderingLayout &&
			renderingLayout.layoutConfig.left.length > 0,
	);
	const data = useToeicItemData(item, {
		renderItemMediaInLeftPanel: shouldRenderItemMediaInLeftPanel,
		includeStimulusMediaInline: !hasStimulusPanel,
	});
	const audioGate = useItemAudioGate(item, isSummativeTest, {
		ttsStem: data.skill === "LISTENING" ? data.stem : undefined,
	});
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const { currentItem } = useCurrentSession();
	const autoOpenPassage =
		currentItem?.id === item.id &&
		item.currentStimulusItemIndex === 1 &&
		prev !== "true" &&
		!inReviewMode;

	if (!renderingLayout) {
		return null;
	}

	const { effectiveSplitLayout, layoutConfig: renderingLayoutConfig } =
		renderingLayout;

	const renderFeedback =
		isDesktopWeb && data.isAnswered
			? () => (
					<StudySessionFeedbackContainer isSplitLayout={effectiveSplitLayout} />
				)
			: undefined;

	const renderHint =
		item.response?.type === "OPTION_SELECTION" &&
		!data.isAnswered &&
		!isSummativeTest
			? () => <HintContainer key={item.id} item={item} />
			: undefined;

	const stem = data.stem;
	const renderTTS =
		data.skill === "LISTENING" && stem
			? () => (
					<TTSContainer
						ref={audioGate?.ttsPlayerRef}
						explanation={stem}
						eventName={events.ttsClicked}
						trackCustomEvent={trackCustomEvent}
						itemId={`${item.id}`}
						allowPrefetch={true}
						playOnce={audioGate?.ttsPlayOnce}
					/>
				)
			: undefined;

	return (
		<ToeicItemRenderer
			data={data}
			itemId={item.id!}
			layoutConfig={renderingLayoutConfig}
			isSplitLayout={effectiveSplitLayout}
			isShellSplitLayout={isSplitLayout}
			isSummativeTest={isSummativeTest}
			renderFeedback={renderFeedback}
			renderHint={renderHint}
			renderTTS={renderTTS}
			audioGate={audioGate}
			autoOpenPassage={autoOpenPassage}
		/>
	);
};

export default ToeicItemContainer;
